/**
 * npeFixationsApi — RTK Query slice cho /purchase/npe-fixations/*.
 * Tag types: 'NPEFixation'. Cũng invalidate 'NPEContract', 'LocalP' vì
 * fixation thay đổi tracking (received/fixed/to_be_fixed) của NPE và sinh LocalP mới.
 *
 * Sprint 4 (SOP §7.7, Table 58) — endpoints:
 *   GET    /purchase/npe-fixations/list?contract_no=&page=&page_size=
 *   POST   /purchase/npe-fixations/create
 *   POST   /purchase/npe-fixations/create-and-allocate
 *   GET    /purchase/npe-fixations/{id}
 *   PUT    /purchase/npe-fixations/{id}
 *   DELETE /purchase/npe-fixations/{id}
 *   GET    /purchase/npe-fixations/{id}/allocations-count
 *   GET    /purchase/npe-fixations/{id}/print
 */
import { baseApi } from './baseApi'
import type {
  DataResponse,
  LocalPPrintResult,
  MessageResponse,
  NPEFixationAllocationsCountOut,
  NPEFixationCreateAndAllocateOut,
  NPEFixationCreateIn,
  NPEFixationListQuery,
  NPEFixationOut,
  NPEFixationSuggestOut,
  NPEFixationUpdateIn,
  PageResponse,
} from '@/types/api'

export const npeFixationsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listNPEFixations: b.query<PageResponse<NPEFixationOut>, NPEFixationListQuery | void>({
      query: (params) => ({
        url: '/purchase/npe-fixations/list',
        params: params ?? {},
      }),
      providesTags: (res, _e, arg) => {
        const contractNo = (arg && (arg as NPEFixationListQuery).contract_no) || 'ALL'
        const list = res
          ? [
              ...res.items.map((f) => ({
                type: 'NPEFixation' as const,
                id: f.cs_fixation_id,
              })),
              { type: 'NPEFixation' as const, id: `LIST-${contractNo}` },
            ]
          : [{ type: 'NPEFixation' as const, id: `LIST-${contractNo}` }]
        return [...list, { type: 'NPEFixation' as const, id: 'LIST' }]
      },
    }),
    getNPEFixation: b.query<DataResponse<NPEFixationOut>, number>({
      query: (id) => `/purchase/npe-fixations/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'NPEFixation', id }],
    }),
    createNPEFixation: b.mutation<DataResponse<NPEFixationOut>, NPEFixationCreateIn>({
      query: (body) => ({
        url: '/purchase/npe-fixations/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'NPEFixation', id: 'LIST' },
        { type: 'NPEContract', id: 'LIST' },
        { type: 'LocalP', id: 'LIST' },
        { type: 'ContractAll', id: 'LIST' },
      ],
    }),
    /**
     * Tạo fixation + LocalP + open allocation session (atomic).
     * Trả về session_uid để FE navigate sang Allocation Wizard.
     */
    createAndAllocateNPEFixation: b.mutation<
      DataResponse<NPEFixationCreateAndAllocateOut>,
      NPEFixationCreateIn
    >({
      query: (body) => ({
        url: '/purchase/npe-fixations/create-and-allocate',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'NPEFixation', id: 'LIST' },
        { type: 'NPEContract', id: 'LIST' },
        { type: 'LocalP', id: 'LIST' },
        { type: 'ContractAll', id: 'LIST' },
      ],
    }),
    updateNPEFixation: b.mutation<
      DataResponse<NPEFixationOut>,
      { id: number; body: NPEFixationUpdateIn }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/npe-fixations/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'NPEFixation', id: arg.id },
        { type: 'NPEFixation', id: 'LIST' },
        { type: 'NPEContract', id: 'LIST' },
        { type: 'LocalP', id: 'LIST' },
      ],
    }),
    deleteNPEFixation: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/npe-fixations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'NPEFixation', id },
        { type: 'NPEFixation', id: 'LIST' },
        { type: 'NPEContract', id: 'LIST' },
        { type: 'LocalP', id: 'LIST' },
      ],
    }),
    /** Số allocations đã commit của 1 fixation. */
    getNPEFixationAllocationsCount: b.query<
      DataResponse<NPEFixationAllocationsCountOut>,
      number
    >({
      query: (id) => `/purchase/npe-fixations/${id}/allocations-count`,
      providesTags: (_r, _e, id) => [
        { type: 'NPEFixation', id: `count-${id}` },
        { type: 'NPEAlloc', id: `fixation-${id}` },
      ],
    }),
    /** Print "PHỤ LỤC HỢP ĐỒNG" — rpPurchaseQryNPEFixation. BE trả HTML qua
     *  PrintResult.html, FE hiển thị qua iframe srcDoc. */
    printNPEFixation: b.query<DataResponse<LocalPPrintResult>, number>({
      query: (id) => `/purchase/npe-fixations/${id}/print`,
    }),
    /** Gợi ý (annex_index, fixation_no) cho NPE Fixation kế tiếp của 1 NPE no.
     *  Access parity với `Suggest_IndexName("phulucindex"|"phuluc" & npe_no)`. */
    suggestNPEFixationNo: b.query<DataResponse<NPEFixationSuggestOut>, string>({
      query: (npe_no) => ({
        url: '/purchase/npe-fixations/suggest',
        params: { npe_no },
      }),
      // Phụ thuộc danh sách fixation hiện có; khi tạo/xóa fixation phải refetch.
      providesTags: (_r, _e, npe_no) => [
        { type: 'NPEFixation', id: `SUGGEST-${npe_no}` },
        { type: 'NPEFixation', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useListNPEFixationsQuery,
  useGetNPEFixationQuery,
  useCreateNPEFixationMutation,
  useCreateAndAllocateNPEFixationMutation,
  useUpdateNPEFixationMutation,
  useDeleteNPEFixationMutation,
  useGetNPEFixationAllocationsCountQuery,
  useLazyPrintNPEFixationQuery,
  useSuggestNPEFixationNoQuery,
} = npeFixationsApi
